import React from 'react';
import { Info, Percent, Scale, Dumbbell, Activity, AlertTriangle, Ruler } from 'lucide-react';

/**
 * Компонент с информацией о процентах жира в организме
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-emerald-500" />
        Всё о проценте жира в организме: показатели, методы измерения, советы
      </h2>
      
      <div className="prose prose-emerald dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Percent className="w-5 h-5 text-emerald-500" />
          Что такое процент жира в организме?
        </h3>
        <p>
          <strong>Процент жира в организме (Body Fat Percentage, BFP)</strong> — это отношение жировой массы к общей массе тела, выраженное в процентах. Этот показатель является более точным индикатором физического состояния, чем индекс массы тела (ИМТ), поскольку учитывает состав тела, а не просто соотношение веса и роста.
        </p>

        <h3 className="flex items-center gap-2">
          <Scale className="w-5 h-5 text-emerald-500" />
          Почему важно знать процент жира?
        </h3>
        <p>
          Знание процента жира в организме помогает:
        </p>
        <ul>
          <li>Более точно оценить состояние здоровья, чем просто вес или ИМТ</li>
          <li>Отслеживать эффективность тренировок и диеты</li>
          <li>Определить риск развития сердечно-сосудистых и других заболеваний</li>
          <li>Установить реалистичные цели для физической формы</li>
          <li>Скорректировать программу питания и физических нагрузок</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-emerald-500" />
          Методы измерения процента жира
        </h3>
        <p>
          Существует несколько методов измерения процента жира, различающихся по точности, стоимости и доступности:
        </p>
        <ul>
          <li>
            <strong>Метод ВМС США (Navy Method)</strong> — основан на измерении окружностей тела. Доступный и простой метод с точностью ±3-4%.
          </li>
          <li>
            <strong>Калиперометрия</strong> — измерение толщины кожных складок специальным инструментом (калипером). Метод Джексона-Поллока использует 3 или 7 точек измерения. Точность ±2-3% при правильном выполнении.
          </li>
          <li>
            <strong>Биоимпедансный анализ (BIA)</strong> — измерение электрического сопротивления тканей тела. Используется в бытовых весах-анализаторах и профессиональных устройствах. Точность зависит от качества оборудования.
          </li>
          <li>
            <strong>Гидростатическое взвешивание</strong> — измерение плотности тела путем взвешивания в воде. Один из самых точных методов, но требует специального оборудования.
          </li>
          <li>
            <strong>DEXA-сканирование</strong> — рентгеновское сканирование, обеспечивает наиболее точные результаты и позволяет увидеть распределение жира. Доступно только в медицинских учреждениях.
          </li>
          <li>
            <strong>3D-сканирование тела</strong> — современный метод, обеспечивающий высокую точность и визуализацию распределения жира.
          </li>
        </ul>

        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 my-6">
          <h4 className="text-blue-700 dark:text-blue-300 font-medium">О точности домашних методов</h4>
          <p className="text-blue-600 dark:text-blue-400 text-sm mb-0">
            Методы измерения в домашних условиях (окружности тела, калиперометрия) дают приближенные результаты и лучше всего подходят для отслеживания изменений с течением времени, а не для определения абсолютно точных значений. Для наиболее точных результатов рекомендуется обратиться к специалисту.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <Ruler className="w-5 h-5 text-emerald-500" />
          Нормы процента жира в организме
        </h3>
        <p>
          Оптимальный процент жира зависит от пола, возраста и уровня физической активности:
        </p>
        
        <h4>Для мужчин:</h4>
        <ul>
          <li><strong>Критически низкий:</strong> менее 3% (опасно для здоровья)</li>
          <li><strong>Уровень атлетов:</strong> 5-10%</li>
          <li><strong>Фитнес-уровень:</strong> 11-14%</li>
          <li><strong>Здоровая норма:</strong> 15-20%</li>
          <li><strong>Избыточный:</strong> 21-24%</li>
          <li><strong>Ожирение:</strong> более 25%</li>
        </ul>
        
        <h4>Для женщин:</h4>
        <ul>
          <li><strong>Критически низкий:</strong> менее 10% (опасно для здоровья)</li>
          <li><strong>Уровень атлетов:</strong> 12-15%</li>
          <li><strong>Фитнес-уровень:</strong> 16-20%</li>
          <li><strong>Здоровая норма:</strong> 21-28%</li>
          <li><strong>Избыточный:</strong> 29-34%</li>
          <li><strong>Ожирение:</strong> более 35%</li>
        </ul>

        <p>
          <em>С возрастом нормальный процент жира немного увеличивается. Для мужчин и женщин старше 40 лет верхняя граница нормы может быть на 2-3% выше.</em>
        </p>

        <h3 className="flex items-center gap-2">
          <Dumbbell className="w-5 h-5 text-emerald-500" />
          Как снизить процент жира?
        </h3>
        <p>
          Снижение процента жира требует комплексного подхода:
        </p>
        <ol>
          <li>
            <strong>Сбалансированное питание:</strong>
            <ul>
              <li>Создание небольшого дефицита калорий (200-500 ккал в день)</li>
              <li>Достаточное потребление белка (1.6-2.2 г на кг веса) для сохранения мышечной массы</li>
              <li>Адекватное потребление жиров (0.5-1 г на кг веса) для гормонального здоровья</li>
              <li>Углеводы преимущественно из сложных источников (овощи, крупы, бобовые)</li>
              <li>Питьевой режим — не менее 30 мл на кг веса</li>
            </ul>
          </li>
          <li>
            <strong>Силовые тренировки:</strong>
            <ul>
              <li>2-4 тренировки в неделю с акцентом на многосуставные упражнения</li>
              <li>Прогрессия нагрузок для стимуляции мышечного роста</li>
              <li>Тренировка всех основных групп мышц</li>
            </ul>
          </li>
          <li>
            <strong>Кардио тренировки:</strong>
            <ul>
              <li>2-3 сессии в неделю по 20-40 минут</li>
              <li>Сочетание высокоинтенсивных интервальных тренировок (HIIT) и тренировок средней интенсивности</li>
            </ul>
          </li>
          <li>
            <strong>Восстановление:</strong>
            <ul>
              <li>Полноценный сон 7-9 часов</li>
              <li>Управление стрессом</li>
              <li>Достаточные перерывы между тренировками</li>
            </ul>
          </li>
        </ol>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Важные предупреждения
          </h4>
          <ul className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            <li>Стремление к слишком низкому проценту жира может быть опасно для здоровья</li>
            <li>Быстрое снижение веса часто приводит к потере мышечной массы, а не жира</li>
            <li>Женщинам особенно важно не снижать процент жира ниже здоровой границы (около 12%), чтобы избежать нарушений гормонального фона</li>
            <li>При наличии хронических заболеваний перед началом программы снижения веса необходима консультация врача</li>
            <li>Для детей и подростков нормы процента жира отличаются, и любые программы по изменению веса должны проводиться под контролем специалистов</li>
          </ul>
        </div>

        <h3>Состав тела и здоровье</h3>
        <p>
          Оптимальный состав тела связан со множеством показателей здоровья:
        </p>
        <ul>
          <li>Снижение риска сердечно-сосудистых заболеваний</li>
          <li>Лучший контроль уровня сахара в крови</li>
          <li>Снижение риска метаболического синдрома</li>
          <li>Улучшение подвижности и снижение нагрузки на суставы</li>
          <li>Повышение энергии и работоспособности</li>
          <li>Улучшение сна и психологического состояния</li>
        </ul>

        <p>
          Важно понимать, что здоровье определяется не только процентом жира, но и его распределением. Висцеральный жир (вокруг внутренних органов) представляет больший риск для здоровья, чем подкожный жир.
        </p>

        <h3>Особенности измерений в России</h3>
        <p>
          В России для оценки процента жира в организме часто используются следующие методы:
        </p>
        <ul>
          <li>
            <strong>Биоимпедансный анализ</strong> — доступен в большинстве фитнес-клубов и медицинских центров. Многие медицинские учреждения используют отечественные приборы, такие как АВС-01 "Медасс".
          </li>
          <li>
            <strong>Калиперометрия</strong> — используется в спортивной медицине и фитнес-центрах. В России часто применяются калиперы отечественного производства.
          </li>
          <li>
            <strong>DEXA-сканирование</strong> — доступно в крупных медицинских центрах и НИИ, особенно в Москве, Санкт-Петербурге и других крупных городах.
          </li>
        </ul>
        
        <p>
          Рекомендуется обращаться в сертифицированные медицинские центры или к специалистам по спортивной медицине для получения наиболее точных результатов.
        </p>

        <p className="font-semibold mt-6">
          Наш калькулятор процента жира в организме поможет вам получить приблизительную оценку состава тела, отслеживать прогресс и корректировать программу тренировок и питания для достижения оптимальных результатов!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;